import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { invitations, testSchemas, questionBanks, auth } from '../api';
import { useToast } from '../components/Toast';
import pricingConfig from '../config/pricing.json';
import { getInvitationStatus, formatInvitationDate } from '../utils/invitationStatus';

function TestInvitations() {
  const { id } = useParams();
  const navigate = useNavigate();
  const showToast = useToast();
  const [invitationList, setInvitationList] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [schemaInfo, setSchemaInfo] = useState(null);
  const [bankInfo, setBankInfo] = useState(null);
  const [error, setError] = useState(null); // Может быть строкой или объектом { message, type }
  const [candidateIdentifier, setCandidateIdentifier] = useState('');
  const [newlyCreatedInvitationId, setNewlyCreatedInvitationId] = useState(null);
  const [isGroupInvitation, setIsGroupInvitation] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [usageLimit, setUsageLimit] = useState('');
  const [tariffInfo, setTariffInfo] = useState(null);
  const [balance, setBalance] = useState(0);

  useEffect(() => {
    loadInvitations();
    loadSchemaInfo();
    loadTariffInfo();
  }, [id]);

  const loadSchemaInfo = async () => {
    try {
      const schemasResponse = await testSchemas.getAll();
      const schema = schemasResponse.data.find(s => s.id === parseInt(id));
      if (schema) {
        setSchemaInfo(schema);
        const banksResponse = await questionBanks.getAll();
        const bank = banksResponse.data.find(b => b.id === schema.question_bank_id);
        if (bank) {
          setBankInfo(bank);
        }
      }
    } catch (err) {
      console.error('Failed to load schema info', err);
    }
  };

  const loadTariffInfo = async () => {
    try {
      const [tariffResponse, meResponse] = await Promise.all([
        auth.getTariffInfo(),
        auth.getMe()
      ]);
      setTariffInfo(tariffResponse.data);
      setBalance(meResponse.data.balance || 0);
    } catch (err) {
      console.error('Failed to load tariff info', err);
    }
  };

  const loadInvitations = async () => {
    try {
      setListLoading(true);
      setListError(null);
      if (!id) {
        console.error('Test schema ID is missing');
        setListError('ID тест-схемы не указан');
        return;
      }
      const response = await invitations.getByTestSchema(id);
      setInvitationList(response.data || []);
    } catch (err) {
      console.error('Failed to load invitations', err);
      setListError('Ошибка загрузки приглашений: ' + (err.response?.data?.detail || err.message));
    } finally {
      setListLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Проверяем наличие токена
      const token = localStorage.getItem('token');
      if (!token) {
        setError({ message: 'Сессия истекла. Пожалуйста, войдите заново.', type: 'general' });
        return;
      }

      let requestData;
      
      if (isGroupInvitation) {
        // Проверяем поля для групповой ссылки
        const trimmedGroupName = groupName.trim();
        if (!trimmedGroupName) {
          setError({ message: 'Пожалуйста, введите название группы', type: 'general' });
          setLoading(false);
          return;
        }
        
        // Валидация лимита использований
        // Если лимит не указан, он будет установлен в 300 на бэкенде
        let parsedUsageLimit = null;
        if (usageLimit && usageLimit.trim() !== '') {
          parsedUsageLimit = parseInt(usageLimit);
          if (isNaN(parsedUsageLimit) || parsedUsageLimit < 1) {
            setError({ message: 'Лимит использований должен быть не менее 1', type: 'general' });
            setLoading(false);
            return;
          }
          if (parsedUsageLimit > 300) {
            setError({ message: 'Максимальный лимит использований групповой ссылки: 300', type: 'general' });
            setLoading(false);
            return;
          }
        }
        // Если не указан, отправляем null - бэкенд установит 300 по умолчанию
        
        requestData = {
          test_schema_id: parseInt(id),
          candidate_title: trimmedGroupName,
          is_group: true,
          group_name: trimmedGroupName,
          usage_limit: parsedUsageLimit, // null если не указан - бэкенд установит 300
        };
      } else {
      // Проверяем, что идентификатор кандидата заполнен
      const trimmedIdentifier = candidateIdentifier.trim();
      if (!trimmedIdentifier) {
          setError({ message: 'Пожалуйста, введите идентификатор кандидата', type: 'general' });
        setLoading(false);
        return;
      }
      
        requestData = {
        test_schema_id: parseInt(id),
        candidate_title: trimmedIdentifier,
          is_group: false,
        };
      }
      
      console.log('Creating invitation with data:', requestData);
      
      const response = await invitations.create(requestData);
      setShowCreate(false);
      setError(null);
      setCandidateIdentifier(''); // Очищаем поле после успешного создания
      setIsGroupInvitation(false);
      setGroupName('');
      setUsageLimit('');
      await loadInvitations();
      await loadTariffInfo(); // Обновляем баланс после создания
      
      // Подсвечиваем кнопку копирования для нового приглашения
      if (response.data && response.data.id) {
        setNewlyCreatedInvitationId(response.data.id);
        // Убираем подсветку через 3 секунды
        setTimeout(() => {
          setNewlyCreatedInvitationId(null);
        }, 3000);
      }
    } catch (err) {
      // Показываем ошибку в UI вместо alert
      console.error('Ошибка создания приглашения:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response,
        request: err.request,
        config: err.config,
      });
      
      let errorMessage = 'Неизвестная ошибка';
      let errorType = 'general'; // 'balance', 'network', 'general'
      
      if (err.response) {
        // Сервер ответил с ошибкой
        const detail = err.response.data?.detail || err.response.data?.message || '';
        errorMessage = detail || `Ошибка сервера: ${err.response.status}`;
        
        // Проверяем, связана ли ошибка с балансом
        if (detail.includes('баланс') || detail.includes('Недостаточно средств') || detail.includes('баланса будет списано')) {
          errorType = 'balance';
        }
      } else if (err.request) {
        // Запрос отправлен, но ответа нет
        errorMessage = 'Нет ответа от сервера. Проверьте подключение к интернету.';
        errorType = 'network';
      } else {
        // Ошибка при настройке запроса
        errorMessage = err.message || 'Ошибка при отправке запроса';
      }
      
      setError({ message: errorMessage, type: errorType });
      // Не закрываем модальное окно, чтобы пользователь видел ошибку
    } finally {
      setLoading(false);
    }
  };

  const getInvitationUrl = (code) => {
    // Генерируем ссылку с использованием домена
    const isProduction = window.location.hostname === 'skillgate.pro' || window.location.hostname === 'www.skillgate.pro';
    const baseUrl = isProduction 
      ? 'https://skillgate.pro'
      : `${window.location.protocol}//${window.location.hostname}:8000`;
    return `${baseUrl}/candidate/index.html?code=${code}`;
  };

  const copyToClipboard = (code) => {
    const url = getInvitationUrl(code);
    navigator.clipboard.writeText(url)
      .then(() => {
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
      })
      .catch(() => showToast('Не удалось скопировать ссылку', 'error'));
  };

  const handleArchive = async (invitationId, isArchived) => {
    try {
      await invitations.archive(invitationId, !isArchived);
      loadInvitations();
    } catch (err) {
      showToast('Ошибка: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  // Функция для подсчета статистики групповой ссылки
  const getGroupInvitationStats = (groupInvitation) => {
    const childInvitations = invitationList.filter(
      inv => inv.parent_group_id === groupInvitation.id
    );
    const completedCount = childInvitations.filter(inv => inv.completed).length;
    return {
      total: childInvitations.length,
      completed: completedCount,
      pending: childInvitations.length - completedCount
    };
  };

  // Группируем приглашения: групповые ссылки и их дочерние приглашения
  const groupInvitations = invitationList.filter(inv => inv.is_group);
  const individualInvitations = invitationList.filter(inv => !inv.is_group && !inv.parent_group_id);
  const childInvitations = invitationList.filter(inv => inv.parent_group_id !== null && inv.parent_group_id !== undefined);
  
  // Создаем структуру для отображения: групповые ссылки с их дочерними приглашениями
  const groupedInvitations = groupInvitations.map(groupInv => {
    const children = childInvitations.filter(child => child.parent_group_id === groupInv.id);
    // Сортируем дочерние приглашения: новые выше
    return {
      group: groupInv,
      children: children.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    };
  });
  
  // Сортируем групповые ссылки: новые выше
  groupedInvitations.sort((a, b) => new Date(b.group.created_at) - new Date(a.group.created_at));
  
  // Фильтруем по архиву
  const activeGroupedInvitations = groupedInvitations.filter(item => !item.group.is_archived);
  const archivedGroupedInvitations = groupedInvitations.filter(item => item.group.is_archived);
  
  const activeIndividualInvitations = individualInvitations
    .filter(inv => !inv.is_archived)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const archivedIndividualInvitations = individualInvitations
    .filter(inv => inv.is_archived)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  
  const displayedGroupedInvitations = showArchived ? archivedGroupedInvitations : activeGroupedInvitations;
  const displayedIndividualInvitations = showArchived ? archivedIndividualInvitations : activeIndividualInvitations;
  
  // Функция для обрезки текста контактов
  const truncateContact = (text, maxLength = 50) => {
    if (!text || text.trim() === '' || text === 'Кандидат') {
      return 'Кандидат';
    }
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };
  
  // Функция для отображения имени кандидата с fallback на код приглашения
  const getCandidateDisplayName = (child) => {
    const contact = truncateContact(child.candidate_title);
    // Если это дефолтное значение "Кандидат" и тест завершен, показываем код приглашения
    if (contact === 'Кандидат' && child.completed) {
      return `Кандидат (${child.invitation_code.substring(0, 8)}...)`;
    }
    return contact;
  };

  return (
    <>
      <div className="mb-6 md:mb-8">
        <button
          onClick={() => navigate('/test-schemas')}
          className="text-blue-600 hover:text-blue-700 mb-4 text-sm font-medium transition-colors"
        >
          ← Назад к тест-схемам
        </button>
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Приглашения кандидатов и результаты</h1>
          <button
            onClick={() => setShowCreate(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
          >
            + Создать приглашение
          </button>
        </div>
        {schemaInfo && bankInfo && (
          <p className="text-gray-600 text-sm md:text-base">
            к тесту по базе данных <strong className="text-blue-600">{bankInfo.name}</strong> (схема: <strong className="text-blue-600">{schemaInfo.name}</strong>)
          </p>
        )}
      </div>

      {/* Модальное окно создания приглашения */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => {
          setShowCreate(false);
          setError(null);
          setCandidateIdentifier(''); // Очищаем поле при закрытии
        }}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">Создание приглашения</h2>
              <button
                onClick={() => {
                  setShowCreate(false);
                  setError(null);
                  setCandidateIdentifier('');
                  setIsGroupInvitation(false);
                  setGroupName('');
                  setUsageLimit('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              {/* Переключатель типа приглашения */}
              <div className="mb-6">
                <div className="flex gap-3 mb-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsGroupInvitation(false);
                      setError(null);
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
                      !isGroupInvitation
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Обычное приглашение
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsGroupInvitation(true);
                      setError(null);
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
                      isGroupInvitation
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Групповая ссылка
                  </button>
                </div>
              </div>

              {isGroupInvitation ? (
                <>
                  <div className="mb-6">
                    <label htmlFor="group-name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Название группы <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="group-name"
                      type="text"
                      value={groupName}
                      onChange={(e) => setGroupName(e.target.value)}
                      placeholder="Например: Набор на должность разработчика"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                      required
                      autoFocus
                      disabled={loading}
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      Название группы для идентификации кандидатов, прошедших тестирование по этой ссылке.
                    </p>
                  </div>
                  <div className="mb-6">
                    <label htmlFor="usage-limit" className="block text-sm font-semibold text-gray-700 mb-2">
                      Лимит использований (необязательно)
                    </label>
                    <input
                      id="usage-limit"
                      type="number"
                      min="1"
                      max="300"
                      value={usageLimit}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Разрешаем пустое значение или число от 1 до 300
                        if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 300)) {
                          setUsageLimit(value);
                        }
                      }}
                      placeholder="Оставьте пустым для безлимита"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                      disabled={loading}
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      Максимальное количество кандидатов, которые могут пройти тест по этой ссылке (от 1 до 300). По умолчанию: 300.
                    </p>
                  </div>
                  {/* Информация о тарифе и балансе */}
                  {tariffInfo && (
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      {(() => {
                        const tariff = pricingConfig.tariffs[tariffInfo.tariff] || {};
                        const groupLimit = tariff.limits?.group_invitations;
                        const overLimitPrice = pricingConfig.over_limit_prices.group_invitation.price;
                        const currentGroupCount = invitationList.filter(inv => inv.is_group).length;
                        
                        if (groupLimit === null) {
                          return (
                            <div className="text-sm">
                              <p className="font-semibold text-blue-900 mb-1">Групповые ссылки не включены в ваш тариф</p>
                              <p className="text-blue-700">
                                При создании групповой ссылки с вашего баланса будет списано <strong>{overLimitPrice}₽</strong>.
                              </p>
                              <p className="text-blue-600 mt-1">Ваш баланс: <strong>{balance.toFixed(2)}₽</strong></p>
                            </div>
                          );
                        } else if (currentGroupCount >= groupLimit) {
                          return (
                            <div className="text-sm">
                              <p className="font-semibold text-orange-900 mb-1">Лимит групповых ссылок исчерпан</p>
                              <p className="text-orange-700">
                                У вас уже {currentGroupCount} из {groupLimit} групповых ссылок. При создании дополнительной будет списано <strong>{overLimitPrice}₽</strong>.
                              </p>
                              <p className="text-orange-600 mt-1">Ваш баланс: <strong>{balance.toFixed(2)}₽</strong></p>
                            </div>
                          );
                        } else {
                          return (
                            <div className="text-sm">
                              <p className="font-semibold text-green-900 mb-1">Групповые ссылки включены в тариф</p>
                              <p className="text-green-700">
                                У вас {currentGroupCount} из {groupLimit} групповых ссылок. Создание бесплатно.
                              </p>
                            </div>
                          );
                        }
                      })()}
                    </div>
                  )}
                </>
              ) : (
              <div className="mb-6">
                <label htmlFor="candidate-identifier" className="block text-sm font-semibold text-gray-700 mb-2">
                  Идентификатор кандидата <span className="text-red-500">*</span>
                </label>
                <input
                  id="candidate-identifier"
                  type="text"
                  value={candidateIdentifier}
                  onChange={(e) => setCandidateIdentifier(e.target.value)}
                  placeholder="Например: Кандидат X, ID-12345, или другое"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                  required
                  autoFocus
                  disabled={loading}
                />
                <p className="mt-2 text-xs text-gray-500">
                  Введите идентификатор для удобства работы с платформой. Персональные данные не собираются целенаправленно.
                </p>
              </div>
              )}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm font-medium">
                    Ошибка: {typeof error === 'string' ? error : error.message}
                  </p>
                  {typeof error === 'object' && error.type === 'balance' ? (
                    <div className="mt-2">
                      <p className="text-red-600 text-xs mb-2">
                        Для создания групповой ссылки необходимо пополнить баланс.
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setShowCreate(false);
                          navigate('/payment');
                        }}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors"
                      >
                        Перейти к оплате →
                      </button>
                    </div>
                  ) : typeof error === 'object' && error.type === 'network' ? (
                  <p className="text-red-600 text-xs mt-1">Проверьте подключение к интернету и попробуйте снова.</p>
                  ) : null}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreate(false);
                    setError(null);
                    setCandidateIdentifier(''); // Очищаем поле при отмене
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all hover:-translate-y-0.5"
                  disabled={loading}
                >
                  {loading ? 'Создание...' : 'Создать приглашение'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Переключатель актуальные/архивные */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={() => setShowArchived(false)}
          className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
            !showArchived
              ? 'bg-blue-600 text-white shadow-md'
              : 'bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50'
          }`}
        >
          Актуальные ({activeGroupedInvitations.length + activeIndividualInvitations.length})
        </button>
        <button
          onClick={() => setShowArchived(true)}
          className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
            showArchived
              ? 'bg-blue-600 text-white shadow-md'
              : 'bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50'
          }`}
        >
          Архив ({archivedGroupedInvitations.length + archivedIndividualInvitations.length})
        </button>
      </div>

      {listLoading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div></div>
      ) : listError ? (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 flex items-center justify-between gap-4">
          <span>{listError}</span>
          <button onClick={loadInvitations} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 flex-shrink-0">Повторить</button>
        </div>
      ) : (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {(displayedGroupedInvitations.length === 0 && displayedIndividualInvitations.length === 0) ? (
          <div className="p-8 text-center">
            <p className="text-gray-600 mb-6">{showArchived ? 'Архив пуст.' : 'Пока нет созданных приглашений. Создайте первое!'}</p>
            {!showArchived && (
              <button
                onClick={() => setShowCreate(true)}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all hover:-translate-y-0.5"
              >
                + Создать приглашение
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b-2 border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Кандидат</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Код приглашения</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-48">Статус</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 w-32">Создано</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Действия</th>
                </tr>
              </thead>
              <tbody>
                {/* Групповые ссылки с дочерними приглашениями */}
                {displayedGroupedInvitations.map(({ group, children }) => {
                  const groupStats = getGroupInvitationStats(group);
                  
                  return (
                    <React.Fragment key={group.id}>
                      {/* Строка групповой ссылки */}
                      <tr className="border-b border-gray-200 bg-blue-50/30 hover:bg-blue-50/50 transition-colors">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {group.group_name || group.candidate_title}
                            </span>
                            <span className="inline-block bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-semibold">
                              Групповая
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col gap-1">
                            <code className="bg-gray-100 px-2 py-1 rounded text-xs break-all">
                              {group.invitation_code}
                            </code>
                            <span className="text-xs text-gray-500">
                              Групповая ссылка - отправьте любому кандидату
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <div className="flex flex-col gap-1 items-center">
                            <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-semibold">
                              Групповая ссылка
                            </span>
                            {groupStats && (
                              <span className="text-xs text-gray-600">
                                Использований: {groupStats.total}
                                {group.usage_limit && ` / ${group.usage_limit}`}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center text-sm text-gray-600">
                          {formatInvitationDate(group.created_at)}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex gap-2 justify-end flex-wrap">
                            <button
                              className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5"
                              onClick={() => copyToClipboard(group.invitation_code)}
                            >
                              {copiedCode === group.invitation_code ? (
                                <>
                                  <span>Скопировано</span>
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                  <span>Групповая ссылка</span>
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => handleArchive(group.id, group.is_archived)}
                              title={group.is_archived ? 'Восстановить из архива' : 'Переместить в архив'}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                group.is_archived
                                  ? 'bg-amber-500 text-white hover:bg-amber-600'
                                  : 'bg-gray-400 text-white hover:bg-gray-500'
                              }`}
                            >
                              {group.is_archived ? 'Восстановить' : 'В архив'}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* Дочерние приглашения (кандидаты из групповой ссылки) */}
                      {children.map((child) => {
                        // Принудительно проверяем completed (как в AllInvitations.js)
                        const isCompleted = Boolean(child.completed) || child.completed === 1 || child.completed === true;
                        const statusInfo = getInvitationStatus(
                          ['passed', 'failed', 'deferred', 'timeout'].includes(child.status)
                            ? child.status
                            : isCompleted ? 'completed' : 'pending'
                        );

                        return (
                        <tr 
                          key={child.id} 
                          className="border-b border-gray-200 hover:bg-gray-50 transition-colors bg-gray-50/50"
                        >
                          <td className="px-4 py-3 pl-8">
                            <span className="text-sm font-medium text-gray-900">
                              {getCandidateDisplayName(child)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1">
                              <code className="bg-gray-100 px-2 py-1 rounded text-xs break-all">
                                {child.invitation_code}
                              </code>
                              <span className="text-xs text-gray-500">
                                {window.location.hostname === 'localhost' 
                                  ? 'Используйте IP адрес для мобильных'
                                  : 'Готово к использованию'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={statusInfo.className}>
                              {statusInfo.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-gray-600">
                            {formatInvitationDate(child.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2 justify-end flex-wrap">
                              {isCompleted ? (
                                <div className="flex items-center gap-1.5">
                                  {child.preliminary_resolution ? (
                                    <span
                                      className={`inline-block px-2 py-0.5 rounded text-xs font-semibold cursor-help ${
                                        child.preliminary_resolution === 'failed'
                                          ? 'bg-red-100 text-red-800'
                                          : 'bg-yellow-100 text-yellow-800'
                                      }`}
                                      title={
                                        child.preliminary_resolution === 'failed'
                                          ? 'Предварительная резолюция: не прошел (автоматическая проверка)'
                                          : 'Предварительная резолюция: под сомнением (автоматическая проверка)'
                                      }
                                    >
                                      {child.preliminary_resolution === 'failed' ? 'Предпроверка: не пройдена' : 'Предпроверка: под сомнением'}
                                    </span>
                                  ) : (
                                    <span
                                      className="inline-block px-2 py-0.5 rounded text-xs font-semibold cursor-help bg-green-100 text-green-800"
                                      title="Предварительная проверка пройдена успешно (автоматическая проверка)"
                                    >
                                      Предпроверка: пройдена
                                    </span>
                                  )}
                                  <button
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                                      !child.status || !['passed', 'failed', 'deferred', 'timeout'].includes(child.status)
                                        ? 'bg-amber-500 text-white hover:bg-amber-600'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                    }`}
                                    onClick={() => navigate(`/test-results/${child.id}`)}
                                    title={!child.status || !['passed', 'failed', 'deferred', 'timeout'].includes(child.status) 
                                      ? "Требуется проверка результатов" 
                                      : "Посмотреть и оценить результаты"}
                                  >
                                    Результаты
                                  </button>
                                  {child.results_viewed === true ? (
                                    <span
                                      className="text-green-600 cursor-help text-lg font-bold"
                                      title="Результаты оплачены и доступны для просмотра"
                                    >
                                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                                    </span>
                                  ) : isCompleted && tariffInfo && tariffInfo.tariff === 'free' ? (
                                    <span
                                      className="text-gray-400 cursor-help text-base"
                                      title="Для просмотра результатов требуется оплата (100 руб.)"
                                    >
                                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                    </span>
                                  ) : null}
                                </div>
                              ) : (
                                <>
                                  <button
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                                      newlyCreatedInvitationId === child.id
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                    onClick={() => copyToClipboard(child.invitation_code)}
                                  >
                                    {copiedCode === child.invitation_code ? (
                                      <>
                                        <span>Скопировано</span>
                                      </>
                                    ) : (
                                      <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        <span>Ссылка-приглашение</span>
                                      </>
                                    )}
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => handleArchive(child.id, child.is_archived)}
                                title={child.is_archived ? 'Восстановить из архива' : 'Переместить в архив'}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                  child.is_archived
                                    ? 'bg-amber-500 text-white hover:bg-amber-600'
                                    : 'bg-gray-400 text-white hover:bg-gray-500'
                                }`}
                              >
                                {child.is_archived ? 'Восстановить' : 'В архив'}
                              </button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                {/* Обычные индивидуальные приглашения (не из групповых ссылок) */}
                {displayedIndividualInvitations.map((invitation) => {
                  const statusInfo = getInvitationStatus(
                    ['passed', 'failed', 'deferred', 'timeout'].includes(invitation.status)
                      ? invitation.status
                      : invitation.completed ? 'completed' : 'pending'
                  );
                  return (
                    <tr 
                      key={invitation.id} 
                      className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-4">
                        <span className="text-sm font-medium text-gray-900">{invitation.candidate_title}</span>
                      </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col gap-1">
                        <code className="bg-gray-100 px-2 py-1 rounded text-xs break-all">
                          {invitation.invitation_code}
                        </code>
                        <span className="text-xs text-gray-500">
                          {window.location.hostname === 'localhost' 
                            ? 'Используйте IP адрес для мобильных'
                            : 'Готово к использованию'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={statusInfo.className}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center text-sm text-gray-600">
                      {formatInvitationDate(invitation.created_at)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-2 justify-end flex-wrap">
                        {invitation.completed ? (
                          <div className="flex items-center gap-1.5">
                            {invitation.preliminary_resolution ? (
                              <span
                                className={`inline-block px-2 py-0.5 rounded text-xs font-semibold cursor-help ${
                                  invitation.preliminary_resolution === 'failed'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}
                                title={
                                  invitation.preliminary_resolution === 'failed'
                                    ? 'Предварительная резолюция: не прошел (автоматическая проверка)'
                                    : 'Предварительная резолюция: под сомнением (автоматическая проверка)'
                                }
                              >
                                {invitation.preliminary_resolution === 'failed' ? 'Предпроверка: не пройдена' : 'Предпроверка: под сомнением'}
                              </span>
                            ) : (
                              <span
                                className="inline-block px-2 py-0.5 rounded text-xs font-semibold cursor-help bg-green-100 text-green-800"
                                title="Предварительная проверка пройдена успешно (автоматическая проверка)"
                              >
                                Предпроверка: пройдена
                              </span>
                            )}
                          <button
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                                !invitation.status || !['passed', 'failed', 'deferred', 'timeout'].includes(invitation.status)
                                  ? 'bg-amber-500 text-white hover:bg-amber-600'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                            onClick={() => navigate(`/test-results/${invitation.id}`)}
                              title={!invitation.status || !['passed', 'failed', 'deferred', 'timeout'].includes(invitation.status) 
                                ? "Требуется проверка результатов" 
                                : "Посмотреть и оценить результаты"}
                          >
                            Результаты
                          </button>
                            {invitation.results_viewed === true ? (
                              <span
                                className="text-green-600 cursor-help text-lg font-bold"
                                title="Результаты оплачены и доступны для просмотра"
                              >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                              </span>
                            ) : invitation.completed && tariffInfo && tariffInfo.tariff === 'free' ? (
                              <span
                                className="text-gray-400 cursor-help text-base"
                                title="Для просмотра результатов требуется оплата (100 руб.)"
                              >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <>
                            <button
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                                newlyCreatedInvitationId === invitation.id
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                              onClick={() => copyToClipboard(invitation.invitation_code)}
                            >
                              {copiedCode === invitation.invitation_code ? (
                                <>
                                  <span>Скопировано</span>
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                  <span>Ссылка-приглашение</span>
                                </>
                              )}
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleArchive(invitation.id, invitation.is_archived)}
                          title={invitation.is_archived ? 'Восстановить из архива' : 'Переместить в архив'}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            invitation.is_archived
                              ? 'bg-amber-500 text-white hover:bg-amber-600'
                              : 'bg-gray-400 text-white hover:bg-gray-500'
                          }`}
                        >
                          {invitation.is_archived ? 'Восстановить' : 'В архив'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}
    </>
  );
}

export default TestInvitations;

